import {RsnSkeleton, RsnTextButton} from '@raison/ui-kit';
import {Atom, useAtomValue} from 'jotai';
import React, {Fragment} from 'react';
import {useNavigate} from 'react-router';
import {Future} from '~/library/types';

export function OtpResendTimer(props: ResendTimerProps) {
    const t = {
        Waiting: "Didn't receive a code? Resend in",
        Resend: 'Resend code',
    };
    return <ResendTimer {...props} t={t} />;
}

export interface Countdown {
    seconds: number;
    attempts: number;
    expiresAtMs: number;
}

export type ResendTimerAtom = Atom<Future<Countdown>>;

export interface ResendTimerProps {
    skeleton?: boolean;
    disabled?: boolean;
    timer$: ResendTimerAtom;
    onRetryIntent: () => void;
}

interface ResendTimerPropsWithText extends ResendTimerProps {
    t: {
        Waiting: string;
        Resend: string;
    };
}

function ResendTimer({timer$, t: texts, disabled, skeleton = false, onRetryIntent}: ResendTimerPropsWithText) {
    const timer = useAtomValue(timer$);
    const navigate = useNavigate();
    const goToSupport = () => {
        navigate('/support/form');
    };

    const t = {
        ...texts,
        ContactSupport: 'Contact support',
        SomethingWrong: 'Something went wrong.',
        TryAgain: 'Try again',
    };

    let content: React.JSX.Element;
    if (timer.pending) {
        content = skeleton ? <RsnSkeleton className='h-5' /> : <div className='h-5' />;
    } else if (!timer.ok) {
        content = (
            <Fragment>
                <span className='text-danger-30'>{t.SomethingWrong}</span>{' '}
                <RsnTextButton.Inline onClick={onRetryIntent}>{t.TryAgain}</RsnTextButton.Inline>
            </Fragment>
        );
    } else if (timer.value.seconds === 0) {
        if (timer.value.attempts === 0) {
            content = (
                <RsnTextButton.Inline onClick={goToSupport} disabled={undefined}>
                    {t.ContactSupport}
                </RsnTextButton.Inline>
            );
        } else {
            content = (
                <RsnTextButton.Inline onClick={onRetryIntent} disabled={disabled}>
                    {t.Resend}
                </RsnTextButton.Inline>
            );
        }
    } else {
        content = (
            <Fragment>
                <span className='text-neutral-60'>{t.Waiting}</span>{' '}
                <span className='fv-text-l-bold text-neutral-100'>{formatTime(timer.value.seconds)}</span>
            </Fragment>
        );
    }
    return <div className='fv-text-l'>{content}</div>;
}

function formatTime(secondsLeft: number) {
    let minutesPart: string | number = Math.floor(secondsLeft / 60);
    let secondsPart: string | number = Math.floor(secondsLeft - minutesPart * 60);
    minutesPart = minutesPart.toString().padStart(2, '0');
    secondsPart = secondsPart.toString().padStart(2, '0');
    return `${minutesPart}:${secondsPart}`;
}
